import React, { useState, useEffect } from 'react';
import { jobListingsAPI } from '../../../services/api';
import RichTextEditor from '../Shared/RichTextEditor';
import '../Shared/FormStyles.scss';

const JOB_TYPES = ['Full-time', 'Part-time', 'Casual', 'Contract', 'Full-time / Part-time', 'Part-time / Casual'];

const JobListingsManager = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    type: 'Full-time',
    description: '',
    order: 0,
    isActive: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const response = await jobListingsAPI.getAllAdmin();
      setListings(response.data);
    } catch (err) {
      setError('Failed to fetch job listings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!formData.description.replace(/<(.|\n)*?>/g, '').trim()) {
      setError('Description is required');
      return;
    }
    try {
      if (editing) {
        await jobListingsAPI.update(editing._id, formData);
        setSuccess('Job listing updated successfully');
      } else {
        await jobListingsAPI.create(formData);
        setSuccess('Job listing created successfully');
      }
      resetForm();
      fetchListings();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save job listing');
    }
  };

  const handleEdit = (listing) => {
    setEditing(listing);
    setFormData({
      title: listing.title || '',
      type: listing.type || 'Full-time',
      description: listing.description || '',
      order: listing.order || 0,
      isActive: listing.isActive !== undefined ? listing.isActive : true,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job listing?')) return;
    try {
      await jobListingsAPI.delete(id);
      setSuccess('Job listing deleted successfully');
      fetchListings();
    } catch (err) {
      setError('Failed to delete job listing');
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({ title: '', type: 'Full-time', description: '', order: 0, isActive: true });
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="job-listings-manager">
      <div className="admin-form">
        <h2>{editing ? 'Edit Job Listing' : 'Add New Job Listing'}</h2>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Job Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="e.g. Physiotherapist"
            />
          </div>
          <div className="form-group">
            <label>Employment Type *</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            >
              {JOB_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Description *</label>
            <RichTextEditor
              value={formData.description}
              onChange={(html) => setFormData({ ...formData, description: html })}
              placeholder="Describe the role, requirements, and any other relevant information..."
            />
          </div>
          <div className="form-group">
            <label>Display Order</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              min="0"
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              {' '}Active (visible on website)
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="dashboard-btn btn-primary">
              {editing ? 'Update Listing' : 'Create Listing'}
            </button>
            {editing && (
              <button type="button" className="dashboard-btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="data-table" style={{ marginTop: '30px' }}>
        <h2 style={{ padding: '20px', margin: 0 }}>All Job Listings ({listings.length})</h2>
        {listings.length === 0 ? (
          <div className="empty-state">No job listings yet. Add your first listing above.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Order</th>
                <th>Title</th>
                <th>Type</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {listings.map((listing) => (
                <tr key={listing._id}>
                  <td>{listing.order}</td>
                  <td>{listing.title}</td>
                  <td>{listing.type}</td>
                  <td>{listing.isActive ? 'Active' : 'Inactive'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn-edit" onClick={() => handleEdit(listing)}>Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(listing._id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default JobListingsManager;
